import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-15bc9094"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "support-case-container" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "label" }, "Subject", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "label" }, "Description", -1))

import { computed, inject, ref, watch } from "vue";
import ModalBase from "../ui/modals/modal-base.vue";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import { DxToolbarItem } from "devextreme-vue/popup";
import { NewSupportCase } from "@/types/utility-switching/NewSupportCase";
import { DxTextArea, DxTextBox, DxValidator } from "devextreme-vue";
import { FieldSize } from "@/enums/system/FieldSize";
import { DxRequiredRule } from "devextreme-vue/validator";
import SupportCaseReasonsSingleSelect from "../ui/dropdowns/utility-switching/support-case-reasons-single-select.vue";
import SecondarySupportCaseReasonsSingleSelect from "../ui/dropdowns/utility-switching/secondary-support-case-reasons-single-select.vue";
import { useSupportCaseReasonsStore } from "@/stores/utility-switching/SupportCaseReasonsStore";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { NewSupportCaseResponse } from "@/types/utility-switching/NewSupportCaseResponse";
import { SupportCaseDocumentType } from "@/enums/utility-switching/SupportCaseDocumentType";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import DocumentUpload from "../file-uploader/document-upload.vue";

const validationGroup = "new-support-case";


export default /*#__PURE__*/_defineComponent({
  __name: 'new-support-case-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for the swith target.
   * @type {string}
   * @default ''
   */
  switchId: {
    type: String,
    default: "",
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes.
   * This is used to update the v-model variable in the parent component and control the visibility of the modal.
   * @event update:modelValue
   * @property {boolean} value - The updated state to reflect whether the modal should be shown or hidden.
   */
  "update:modelValue",

  /**
   * Triggered when the new case creation operation is successful.
   * This informs the parent component that the operation is success.
   */
  "newCaseCreated",
],
  setup(__props, { emit }) {

const props = __props;





const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const supportCase = ref<NewSupportCase>({
  switchID: "",
  reason: "",
  secondaryReason: "",
  subject: "",
  description: "",
});

const reasonId = ref(-1);
const secondaryReasonId = ref(-1);
const documents = ref<File[]>();
const tcf = ref<File[]>();
const saving = ref(false);

const caseReasonsStore = useSupportCaseReasonsStore();
const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;

/** Handles the Click event of the Save button */
const onSaveClick = async (e: any) => {
  const validate = e.validationGroup.validate();

  if (!validate.isValid) {
    return;
  }

  if (await saveDetails()) {
    emit("newCaseCreated");
    showModal.value = false;
  }
};

/** Calls apis to save new Support Case and upload attached documents */
const saveDetails = async () => {
  try {
    saving.value = true;

    const response = await createSupportCase();

    // Upload any attached files
    if (documents.value) {
      for (let i = 0; i < documents.value.length; i++) {
        await uploadDocument(
          response.homeMoverCaseID,
          SupportCaseDocumentType.Other,
          documents.value[i],
        );
      }
    }

    if (tcf.value && tcf.value.length > 0) {
      await uploadDocument(
        response.homeMoverCaseID,
        SupportCaseDocumentType.Tcf,
        tcf.value[0],
      );
    }

    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  } finally {
    saving.value = false;
  }
};

/** Calls the New Support Case endpoint to create a Support Case */
const createSupportCase = async () => {
  return await apiClient.httpPost<NewSupportCaseResponse>(
    "app/utilityswitching/supportcase",
    supportCase.value,
  );
};

/** Uploads a document for the newly created support case
 * @param supportCaseId Id of the Support Case to upload the document to
 * @param documentType Type of document being uploaded
 * @param document The document to upload to the Support Case */
const uploadDocument = async (
  supportCaseId: string,
  documentType: SupportCaseDocumentType,
  document: File,
) => {
  await apiClient.uploadFile(
    `app/utilityswitching/supportcases/documents/${supportCaseId}/${documentType}`,
    document,
  );
};

/** Handles the Click event of the Cancel button */
const onCancelClick = () => {
  showModal.value = false;
};

// When modal is closed/opened reset data to avoid confusing users
watch(
  () => props.modelValue,
  () => {
    supportCase.value = {
      switchID: props.switchId,
      reason: "",
      secondaryReason: "",
      subject: "",
      description: "",
    };

    secondaryReasonId.value = -1;

    /* Ensure reason id is properly reset by changing the value based on whether the modal is shown or not
     * This prevents a blank reason from being sent to the api, which causes a bad request error*/
    reasonId.value = props.modelValue ? 0 : -1;

    tcf.value = [];
    documents.value = [];
  },
);

/* New Support Case api requires the description of the reason to be sent instead of the id
 * This is due to how the salesforce api works. To avoid issues, when the reason changes, get the description */
watch(
  () => reasonId.value,
  async () => {
    supportCase.value.reason =
      await caseReasonsStore.getPrimarySupportCaseReasonDescription(
        reasonId.value,
      );

    // We need to manually trigger this due to a race condition with the watch on the secondaryReasonId
    supportCase.value.secondaryReason =
      await caseReasonsStore.getSecondarySupportCaseReasonDescription(
        reasonId.value,
        secondaryReasonId.value,
      );
  },
  { immediate: true },
);

/** Similar to the primary case reason, we need to get the description
 * so it can be sent to the New Support Case api */
watch(
  () => secondaryReasonId.value,
  async () => {
    supportCase.value.secondaryReason =
      await caseReasonsStore.getSecondarySupportCaseReasonDescription(
        reasonId.value,
        secondaryReasonId.value,
      );
  },
  { immediate: true },
);

/** Handles the Document Changed event of the Document upload component */
const onDocumentsChanged = (e: File[]) => {
  documents.value = e;
};

/** Handles the TCF Changed event of the Document upload component */
const onTcfChanged = (e: File[]) => {
  tcf.value = e;
};

watch(
  () => showModal.value,
  () => {
    resetUploadedFiles.value = !showModal.value;
  },
);

const onModalClosed = () => {
  // To avoid previous validation errors being displayed, reset the validator when the modal closes
  if (validator.value?.instance) {
    validator.value.instance.reset();
  }
};

const resetUploadedFiles = ref(false);

const validator = ref<DxValidator>();

const onSubjectFocusOut = () => {
  supportCase.value.subject = supportCase.value.subject.trim();
};

const onDescriptionFocusOut = () => {
  supportCase.value.description = supportCase.value.description.trim();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalBase, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showModal).value = $event)),
    title: "New Support Case",
    width: _unref(ModalWidth).Medium,
    height: "75vh",
    onModalClosed: onModalClosed
  }, {
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(SupportCaseReasonsSingleSelect, {
          modelValue: reasonId.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((reasonId).value = $event)),
          label: "Reason",
          "read-only": saving.value,
          "validation-group": validationGroup
        }, null, 8, ["modelValue", "read-only"]),
        _createVNode(SecondarySupportCaseReasonsSingleSelect, {
          modelValue: secondaryReasonId.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((secondaryReasonId).value = $event)),
          "read-only": saving.value,
          "primary-support-case-reason-id": reasonId.value,
          label: "Secondary Reason",
          "validation-group": validationGroup
        }, null, 8, ["modelValue", "read-only", "primary-support-case-reason-id"]),
        _createElementVNode("div", null, [
          _hoisted_2,
          _createVNode(_unref(DxTextBox), {
            value: supportCase.value.subject,
            "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((supportCase.value.subject) = $event)),
            width: _unref(FieldSize).Full,
            "read-only": saving.value,
            onFocusOut: _cache[3] || (_cache[3] = ($event: any) => (onSubjectFocusOut()))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DxValidator), {
                ref_key: "validator",
                ref: validator,
                "validation-group": validationGroup
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DxRequiredRule), { message: "" })
                ]),
                _: 1
              }, 512)
            ]),
            _: 1
          }, 8, ["value", "width", "read-only"])
        ]),
        _createElementVNode("div", null, [
          _hoisted_3,
          _createVNode(_unref(DxTextArea), {
            modelValue: supportCase.value.description,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((supportCase.value.description) = $event)),
            height: "175px",
            width: _unref(FieldSize).Full,
            "read-only": saving.value,
            onFocusOut: _cache[5] || (_cache[5] = ($event: any) => (onDescriptionFocusOut()))
          }, null, 8, ["modelValue", "width", "read-only"])
        ]),
        _createVNode(DocumentUpload, {
          "read-only": saving.value,
          onDocumentsChanged: onDocumentsChanged,
          onTcfChanged: onTcfChanged,
          reset: resetUploadedFiles.value
        }, null, 8, ["read-only", "reset"])
      ])
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxLoadIndicator",
        toolbar: "bottom",
        location: "after",
        options: { visible: saving.value, width: 24, height: 24 }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Save',
          disabled: saving.value,
          validationGroup: validationGroup,
          onClick: (e) => {
            onSaveClick(e);
          },
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Cancel',
          disabled: saving.value,
          onClick: () => {
            onCancelClick();
          },
        },
        shortcut: "cancel"
      }, null, 8, ["options"])
    ]),
    _: 1
  }, 8, ["modelValue", "width"]))
}
}

})